import React, { useEffect } from 'react';
import { X, UploadCloud, CheckCircle, AlertCircle, MinusCircle, Maximize2, Minimize2, Moon, Sun } from 'lucide-react';
import type { LoadResult } from '../../types/rosterFlow';

// ─── Shared CSS ──────────────────────────────────────────────────────────────

const injectStyles = () => {
  if (typeof document !== 'undefined' && !document.getElementById('load-modal-styles')) {
    const el = document.createElement('style');
    el.id = 'load-modal-styles';
    el.textContent = `
      @keyframes load-fadeIn { from{opacity:0}to{opacity:1} }
      @keyframes load-slideUp {
        from{opacity:0;transform:translateY(32px) scale(0.97)}
        to  {opacity:1;transform:translateY(0)    scale(1)   }
      }
      .load-tr:hover td{ background:rgba(0,38,119,0.025); }
    `;
    document.head.appendChild(el);
  }
};

// ─── Step Indicator ──────────────────────────────────────────────────────────

const FLOW_STEPS = ['Column Mapping', 'Validation', 'Load'];

const StepIndicator: React.FC<{ current: number; completedSteps?: boolean[]; onNavigate?: (step: number) => void }> = ({ current, completedSteps = [], onNavigate }) => (
  <div style={{ display:'flex', alignItems:'center' }}>
    {FLOW_STEPS.map((label, i) => {
      const isDone = completedSteps[i] === true;
      const active = i === current;
      const canNav = isDone && !active && !!onNavigate;
      return (
        <React.Fragment key={i}>
          <div
            onClick={canNav ? () => onNavigate!(i) : undefined}
            title={canNav ? `Go to ${label}` : undefined}
            style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:3, cursor: canNav ? 'pointer' : 'default' }}
          >
            <div style={{
              width:26, height:26, borderRadius:'50%',
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:11, fontWeight:700,
              background: active ? 'white' : isDone ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.2)',
              color: active || isDone ? '#002677' : 'rgba(255,255,255,0.6)',
              boxShadow: active ? '0 0 0 3px rgba(255,255,255,0.3)' : canNav ? '0 0 0 2px rgba(255,255,255,0.4)' : 'none',
            }}>
              {i < current ? <CheckCircle size={13} color="#002677" /> : i + 1}
            </div>
            <span style={{ fontSize:10, fontWeight: active ? 700 : isDone ? 600 : 400, color: active ? 'white' : isDone ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.4)', whiteSpace:'nowrap' }}>{label}</span>
          </div>
          {i < FLOW_STEPS.length - 1 && (
            <div style={{ width:36, height:2, background: completedSteps[i] ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.2)', margin:'0 4px 13px' }} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ─── Row status badge ────────────────────────────────────────────────────────

const LoadBadge: React.FC<{ status: string }> = ({ status }) => {
  if (status === 'loaded') return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'4px 10px', borderRadius:20, background:'#f0fdf4', color:'#15803d', fontSize:11, fontWeight:700, border:'1px solid #bbf7d0' }}>
      <CheckCircle size={11} /> Loaded
    </span>
  );
  if (status === 'failed') return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'4px 10px', borderRadius:20, background:'#fef2f2', color:'#b91c1c', fontSize:11, fontWeight:700, border:'1px solid #fecaca' }}>
      <AlertCircle size={11} /> Failed
    </span>
  );
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'4px 10px', borderRadius:20, background:'#f8fafc', color:'#64748b', fontSize:11, fontWeight:700, border:'1px solid #e2e8f0' }}>
      <MinusCircle size={11} /> Skipped
    </span>
  );
};

// ─── Component ───────────────────────────────────────────────────────────────

export interface LoadModalProps {
  isOpen: boolean;
  data: LoadResult | null;
  fileName: string;
  onClose: () => void;
  /** Called when the user clicks the Done button (after load completes). */
  onDone?: () => void;
  /** [columnMapping, validation, load] — true means that stage has data and can be navigated to. */
  completedSteps?: boolean[];
  /** Called when the user clicks a completed stage in the step indicator to navigate there. */
  onNavigate?: (step: number) => void;
}

export const LoadModal: React.FC<LoadModalProps> = ({ isOpen, data, fileName, onClose, onDone, completedSteps = [], onNavigate }) => {
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [isDark, setIsDark] = React.useState(false);
  useEffect(() => { injectStyles(); }, []);

  if (!isOpen || !data) return null;

  const { totalRecords, loadedRecords, failedRecords, skippedRecords, durationMs, records } = data;
  const successRate = totalRecords > 0 ? Math.round((loadedRecords / totalRecords) * 100) : 0;
  const durationSec = (durationMs / 1000).toFixed(1);

  const t = isDark ? {
    bodyBg:       'linear-gradient(145deg,#0f172a 0%,#1a2744 100%)',
    theadBg:      '#0f172a', theadColor:'#93c5fd', theadBorder:'#334155',
    rowBorder:    '#1e293b', rowNumColor:'#475569',
    memberColor:  '#e2e8f0',
    msgColor:     '#94a3b8',
    bannerSuccessBg:'#052e16', bannerSuccessBorder:'#166534',
    bannerErrBg:  '#431407',  bannerErrBorder:'#9a3412',
    footerBg:     '#0f172a',  footerBorder:'#334155', footerText:'#94a3b8',
    closeBtnBg:   'linear-gradient(135deg,#1e3a5f,#1d4ed8)',
  } : {
    bodyBg:       'linear-gradient(145deg,#ffffff 0%,#f8fafc 100%)',
    theadBg:      '#f1f5f9', theadColor:'#1e40af', theadBorder:'#e5e7eb',
    rowBorder:    '#f1f5f9', rowNumColor:'#94a3b8',
    memberColor:  '#0f172a',
    msgColor:     '#64748b',
    bannerSuccessBg:'#f0fdf4', bannerSuccessBorder:'#bbf7d0',
    bannerErrBg:  '#fff7ed',  bannerErrBorder:'#fed7aa',
    footerBg:     '#f9fafb',  footerBorder:'#e5e7eb', footerText:'#64748b',
    closeBtnBg:   'rgb(0,38,119)',
  };

  return (
    <div style={{
      position:'fixed', inset:0,
      background:'linear-gradient(135deg,rgba(15,23,42,0.92) 0%,rgba(30,41,59,0.88) 100%)',
      backdropFilter:'blur(16px) saturate(180%)',
      display:'flex', alignItems:'center', justifyContent:'center',
      zIndex:999999, padding: isFullscreen ? 0 : 20,
      animation:'load-fadeIn 0.25s ease',
    }}>
      <div style={{
        background: t.bodyBg,
        borderRadius: isFullscreen ? 0 : 24, overflow:'hidden',
        display:'flex', flexDirection:'column',
        width: isFullscreen ? '100vw' : '94vw',
        maxWidth: isFullscreen ? '100vw' : 920,
        height: isFullscreen ? '100vh' : '88vh',
        boxShadow:'0 40px 100px rgba(15,23,42,0.5),0 0 0 1px rgba(255,255,255,0.08)',
        animation:'load-slideUp 0.35s cubic-bezier(0.34,1.56,0.64,1)',
      }}>

        {/* ── Header ── */}
        <div style={{ background:'rgb(0,38,119)', padding:'20px 28px', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:18, position:'relative' }}>
            <div style={{ display:'flex', alignItems:'center', gap:14 }}>
              <div style={{ width:44, height:44, borderRadius:11, background:'rgba(255,255,255,0.18)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <UploadCloud size={22} color="white" />
              </div>
              <div>
                <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:'white', letterSpacing:'-0.3px' }}>Data Load</h2>
                <p style={{ margin:'3px 0 0', fontSize:12, color:'rgba(255,255,255,0.7)' }}>{fileName}</p>
              </div>
            </div>
            <div style={{ position:'absolute', left:'50%', transform:'translateX(-50%)' }}>
              <StepIndicator current={2} completedSteps={completedSteps} onNavigate={onNavigate} />
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <button onClick={() => setIsDark(d => !d)} title={isDark ? 'Light mode' : 'Dark mode'} style={{ width:34, height:34, borderRadius:9, border:'none', background:'rgba(255,255,255,0.15)', color:'white', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                {isDark ? <Sun size={16} /> : <Moon size={16} />}
              </button>
              <button onClick={() => setIsFullscreen(f => !f)} title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} style={{ width:34, height:34, borderRadius:9, border:'none', background:'rgba(255,255,255,0.15)', color:'white', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </button>
              <button onClick={onClose} style={{ width:34, height:34, borderRadius:9, border:'none', background:'rgba(255,255,255,0.15)', color:'white', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <X size={17} />
              </button>
            </div>
          </div>

          {/* Summary cards */}
          <div style={{ display:'flex', gap:10 }}>
            {[
              { label:'Total Records',    value: totalRecords,   color:'rgba(255,255,255,0.95)' },
              { label:'Loaded',           value: loadedRecords,  color:'#86efac' },
              { label:'Failed',           value: failedRecords,  color: failedRecords > 0 ? '#fca5a5':'rgba(255,255,255,0.95)' },
              { label:'Success Rate',     value: `${successRate}%`, color: successRate >= 90 ? '#86efac' : successRate >= 70 ? '#fde68a' : '#fca5a5' },
              { label:'Duration',         value: `${durationSec}s`, color:'rgba(255,255,255,0.95)' },
            ].map(s => (
              <div key={s.label} style={{ flex:1, background:'rgba(255,255,255,0.1)', borderRadius:9, padding:'9px 13px', border:'1px solid rgba(255,255,255,0.14)' }}>
                <div style={{ fontSize:20, fontWeight:800, color:s.color, lineHeight:1 }}>{s.value}</div>
                <div style={{ fontSize:11, color:'rgba(255,255,255,0.6)', marginTop:3, fontWeight:500 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Result banner ── */}
        <div style={{
          margin:'16px 24px 0', padding:'12px 18px', borderRadius:12,
          background: failedRecords === 0 ? (isDark ? '#0a1628' : '#ffffff') : (isDark ? '#1a0a00' : '#ffffff'),
          border: `1px solid ${failedRecords === 0 ? (isDark ? '#1e3a5f' : '#e2e8f0') : (isDark ? '#7c2d12' : '#e2e8f0')}`,
          borderLeft: `4px solid ${failedRecords === 0 ? '#22c55e' : '#ef4444'}`,
          display:'flex', alignItems:'center', gap:12, flexShrink:0,
          boxShadow: isDark ? 'none' : '0 1px 6px rgba(15,23,42,0.06)',
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10, flexShrink: 0,
            background: failedRecords === 0 ? (isDark ? 'rgba(34,197,94,0.15)' : '#f0fdf4') : (isDark ? 'rgba(239,68,68,0.15)' : '#fef2f2'),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {failedRecords === 0
              ? <CheckCircle size={18} color="#22c55e" />
              : <AlertCircle size={18} color="#ef4444" />}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a', marginBottom: 2 }}>
              {failedRecords === 0 ? 'All records loaded successfully' : `${failedRecords} record(s) failed to load`}
            </div>
            <div style={{ fontSize: 11, color: isDark ? '#64748b' : '#94a3b8' }}>
              {failedRecords === 0 ? `${loadedRecords} records processed in ${durationSec}s` : 'Review the table below for details'}
            </div>
          </div>
        </div>

        {/* ── Table ── */}
        <div style={{ flex:1, overflowY:'auto', padding: '16px 24px 20px' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 13,
            borderRadius: 12,
            overflow: 'hidden',
            boxShadow: isDark ? 'none' : '0 1px 6px rgba(15,23,42,0.06)',
          }}>
            <thead>
              <tr>
                {[
                  { label: '#',       w: '52px'  },
                  { label: 'NPI',     w: '200px' },
                  { label: 'Status',  w: '120px' },
                  { label: 'Message', w: 'auto'  },
                ].map(h => (
                  <th key={h.label} style={{
                    width: h.w,
                    padding: '11px 16px',
                    textAlign: 'left',
                    fontSize: 11, fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: isDark ? '#475569' : '#94a3b8',
                    background: isDark ? '#0f172a' : '#f8fafc',
                    borderBottom: `2px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                    whiteSpace: 'nowrap',
                  }}>{h.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {records.map((rec, idx) => {
                const isFailed = rec.status === 'failed';
                const isLast = idx === records.length - 1;
                const rowBg = isDark
                  ? (idx % 2 === 0 ? '#1e293b' : '#172032')
                  : (idx % 2 === 0 ? '#ffffff' : '#f9fafb');
                const tdBase: React.CSSProperties = {
                  padding: '12px 16px',
                  borderBottom: isLast ? 'none' : `1px solid ${isDark ? '#1e293b' : '#f1f5f9'}`,
                  background: rowBg,
                  verticalAlign: 'middle',
                };
                return (
                  <tr
                    key={rec.rowNumber}
                    onMouseEnter={e => Array.from((e.currentTarget as HTMLTableRowElement).cells).forEach(c => { (c as HTMLElement).style.background = isDark ? '#1e3a5f' : '#eff6ff'; })}
                    onMouseLeave={e => Array.from((e.currentTarget as HTMLTableRowElement).cells).forEach(c => { (c as HTMLElement).style.background = rowBg; })}
                  >
                    {/* # */}
                    <td style={{ ...tdBase, color: isDark ? '#475569' : '#94a3b8', fontWeight: 600, fontSize: 12 }}>
                      {rec.rowNumber}
                    </td>

                    {/* NPI */}
                    <td style={tdBase}>
                      <code style={{
                        fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                        fontSize: 12, fontWeight: 600,
                        color: isDark ? '#93c5fd' : '#1e40af',
                        background: isDark ? 'rgba(147,197,253,0.08)' : '#eff6ff',
                        padding: '3px 8px', borderRadius: 6,
                        border: `1px solid ${isDark ? 'rgba(147,197,253,0.15)' : '#bfdbfe'}`,
                        letterSpacing: '0.04em',
                      }}>
                        {rec.memberId || '—'}
                      </code>
                    </td>

                    {/* Status */}
                    <td style={tdBase}>
                      <LoadBadge status={rec.status} />
                    </td>

                    {/* Message */}
                    <td style={{
                      ...tdBase,
                      fontSize: 12,
                      color: isFailed
                        ? (isDark ? '#fca5a5' : '#dc2626')
                        : (isDark ? '#475569' : '#94a3b8'),
                      fontStyle: rec.message ? 'normal' : 'italic',
                      maxWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {rec.message || 'No message'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ── Footer ── */}
        <div style={{ padding:'16px 24px', background: t.footerBg, borderTop:`1px solid ${t.footerBorder}`, display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div style={{ fontSize:13, color: t.footerText }}>
            <strong style={{ color: isDark ? '#93c5fd' : '#002677' }}>{loadedRecords}</strong> record(s) loaded in <strong>{durationSec}s</strong>
            {skippedRecords > 0 && <span style={{ marginLeft:6, color: isDark ? '#475569' : '#94a3b8' }}>· {skippedRecords} skipped</span>}
          </div>
          <button
            onClick={() => { onClose(); onDone?.(); }}
            style={{ padding:'11px 32px', borderRadius:12, border:'1.5px solid rgba(134,239,172,0.3)', background:'linear-gradient(135deg,#0f2060 0%,#1e3a8a 100%)', color:'white', fontSize:14, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:10, boxShadow:'0 4px 16px rgba(15,32,96,0.35)', letterSpacing:'0.015em', transition:'transform 0.15s,box-shadow 0.15s,filter 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 8px 24px rgba(15,32,96,0.48)'; e.currentTarget.style.filter='brightness(1.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='0 4px 16px rgba(15,32,96,0.35)'; e.currentTarget.style.filter='brightness(1)'; }}
          >
            <CheckCircle size={16} color="#86efac" /> Done
          </button>
        </div>
      </div>
    </div>
  );
};
